import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import * as ActionTypes from '../actions/types';
import GENERAL_FUNCTIONS from '../utils/functions/functions';
import {
    acceptClaimReviewSuccessAction,
    ClaimReviewDetails,
    fetchClaimReviewDetailsSuccessAction,
    fetchClaimReviewValiditySuccessAction,
    fetchRejectionFormClaimReviewSuccessAction,
    rejectClaimReviewWithReasonSuccessAction,
} from '../actions/claimReview';
interface ClaimOverview {
    claimReference: string;
    policyTypeName: string;
    claimCategoryName: string;
    claimTypeName: string;
    formTypeName: string;
}
type State = {
    claimReview: ClaimReviewDetails;
    claimReviewDetails: {
        question: {
            byId: { [id: string]: any };
            allIds: Array<string> | [];
        };
        questionType: {
            byId: { [id: string]: any } | null;
            allIds: Array<string> | [] | null;
        };
        questionGroupStep: {
            byId: { [id: string]: any } | null;
            allIds: Array<string> | [] | null;
        };
        answers: null | Array<any>;
        currencies: null | Array<any>;
        overview: null | ClaimOverview;
    };

    claimReviewAcceptedRejectedWordings:
        | {
              byId: { [id: string]: any };
              allIds: Array<string> | [];
          }
        | any;
    rejectedReasonClaimReview: null | Array<string>;
    isClaimReviewValid: null | boolean;
    loading: boolean;
    errorMessage: null | any;
    submitting: boolean;
    rejectFormLoading: boolean;
};
const INITIAL_STATE: State = {
    claimReview: {
        claimApplicationId: null,
        randomId: null,
        email: null,
        passCode: null,
    },
    claimReviewDetails: {
        question: {
            byId: {},
            allIds: [],
        },
        questionType: {
            byId: null,
            allIds: null,
        },
        questionGroupStep: {
            byId: {},
            allIds: [],
        },
        answers: null,
        overview: null,
        currencies: null,
    },
    claimReviewAcceptedRejectedWordings: null,
    rejectedReasonClaimReview: null,
    isClaimReviewValid: null,
    loading: false,
    errorMessage: null,
    submitting: false,
    rejectFormLoading: false,
};

const fetchClaimReviewValidity = (state: State): State => {
    return {
        ...state,
        loading: true,
        errorMessage: null,
    };
};

const fetchClaimReviewValiditySuccess = (
    state: State,
    action: PayloadAction<fetchClaimReviewValiditySuccessAction>,
): State => {
    return {
        ...state,
        loading: false,
        isClaimReviewValid: action.payload.body.data,
        errorMessage: null,
    };
};

const fetchClaimReviewValidityFailed = (state: State): State => {
    return {
        ...state,
        loading: false,
        isClaimReviewValid: false,
        errorMessage: null,
    };
};

const fetchClaimReviewDetails = (state: State): State => {
    return {
        ...state,
        submitting: true,
    };
};

const fetchClaimReviewDetailsSuccess = (
    state: State,
    action: PayloadAction<fetchClaimReviewDetailsSuccessAction>,
): State => {
    let response = {
        question: {
            byId: {},
            allIds: [],
        },
        questionType: {
            byId: {},
            allIds: [],
        },
        questionGroupStep: {
            byId: {},
            allIds: [],
        },
        answers: null,
        currencies: [],
    };

    if (action.payload.body.data.questions) {
        const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
            'code',
            action.payload.body.data.questions,
        );

        response = {
            ...response,
            question: {
                ...response.question,
                byId,
                allIds: allIds as any,
            },
        };
    }

    if (action.payload.body.data.questionGroupSteps) {
        const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
            'code',
            action.payload.body.data.questionGroupSteps,
        );

        response = {
            ...response,
            questionGroupStep: {
                ...response.questionGroupStep,
                byId,
                allIds: allIds as any,
            },
        };
    }

    if (action.payload.body.data.questionTypes) {
        const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
            'id',
            action.payload.body.data.questionTypes,
        );

        response = {
            ...response,
            questionType: {
                ...response.questionType,
                byId,
                allIds: allIds as any,
            },
        };
    }

    return {
        ...state,
        submitting: false,
        claimReviewDetails: {
            ...response,
            answers: action.payload.body.data.answers,
            currencies: action.payload.body.data.currencies,
            overview: {
                claimReference: action.payload.body.data.claimReference,
                policyTypeName: action.payload.body.data.policyTypeName,
                claimCategoryName: action.payload.body.data.claimCategoryName,
                claimTypeName: action.payload.body.data.claimTypeName,
                formTypeName: action.payload.body.data.formTypeName,
            },
        },
    };
};

const fetchClaimReviewDetailsFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
    };
};

const acceptClaimReview = (state: State): State => {
    return {
        ...state,
        submitting: true,
        errorMessage: null,
    };
};

const acceptClaimReviewSuccess = (
    state: State,
    action: PayloadAction<acceptClaimReviewSuccessAction>,
): State => {
    const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
        'languageId',
        action.payload?.body?.data,
    );

    return {
        ...state,
        submitting: false,
        claimReviewAcceptedRejectedWordings: {
            byId,
            allIds,
        },
        errorMessage: null,
    };
};

const acceptClaimReviewFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
    };
};

const fetchRejectionFormClaimReview = (state: State): State => {
    return {
        ...state,
        rejectFormLoading: true,
        errorMessage: null,
    };
};

const fetchRejectionFormClaimReviewSuccess = (
    state: State,
    action: PayloadAction<fetchRejectionFormClaimReviewSuccessAction>,
) => {
    return {
        ...state,
        rejectFormLoading: false,
        rejectedReasonClaimReview: action.payload.body.data,
        errorMessage: null,
    };
};

const fetchRejectionFormClaimReviewFailed = (state: State): State => {
    return {
        ...state,
        rejectFormLoading: false,
    };
};

const rejectClaimReviewWithReason = (state: State): State => {
    return {
        ...state,
        submitting: true,
        errorMessage: null,
    };
};

const rejectClaimReviewWithReasonSuccess = (
    state: State,
    action: PayloadAction<rejectClaimReviewWithReasonSuccessAction>,
): State => {
    const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
        'languageId',
        action.payload?.body?.data,
    );

    return {
        ...state,
        submitting: false,
        claimReviewAcceptedRejectedWordings: {
            byId,
            allIds,
        },
    };
};

const rejectClaimReviewWithReasonFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
        errorMessage: null,
    };
};

const changeClaimReviewAnswerStateAction = (
    state: State,
    action: PayloadAction<any>,
): State => {
    return {
        ...state,
        claimReview: {
            ...state.claimReview,
            ...action.payload,
        },
    };
};

const changeClaimReviewStateAction = (
    state: State,
    action: PayloadAction<any>,
): State => {
    return {
        ...state,
        ...action.payload,
    };
};

const claimReview = createReducer(INITIAL_STATE, {
    [ActionTypes.FETCH_CLAIM_REVIEW_VALIDITY]: fetchClaimReviewValidity,
    [ActionTypes.FETCH_CLAIM_REVIEW_VALIDITY_SUCCESS]:
        fetchClaimReviewValiditySuccess,
    [ActionTypes.FETCH_CLAIM_REVIEW_VALIDITY_FAILED]:
        fetchClaimReviewValidityFailed,
    [ActionTypes.FETCH_CLAIM_REVIEW_DETAILS]: fetchClaimReviewDetails,
    [ActionTypes.FETCH_CLAIM_REVIEW_DETAILS_SUCCESS]:
        fetchClaimReviewDetailsSuccess,
    [ActionTypes.FETCH_CLAIM_REVIEW_DETAILS_FAILED]:
        fetchClaimReviewDetailsFailed,
    [ActionTypes.ACCEPT_CLAIM_REVIEW]: acceptClaimReview,
    [ActionTypes.ACCEPT_CLAIM_REVIEW_SUCCESS]: acceptClaimReviewSuccess,
    [ActionTypes.ACCEPT_CLAIM_REVIEW_FAILED]: acceptClaimReviewFailed,
    [ActionTypes.FETCH_REJECTION_FORM_CLAIM_REVIEW]:
        fetchRejectionFormClaimReview,
    [ActionTypes.FETCH_REJECTION_FORM_CLAIM_REVIEW_SUCCESS]:
        fetchRejectionFormClaimReviewSuccess,
    [ActionTypes.FETCH_REJECTION_FORM_CLAIM_REVIEW_FAILED]:
        fetchRejectionFormClaimReviewFailed,
    [ActionTypes.REJECT_CLAIM_REVIEW_WITH_REASON]: rejectClaimReviewWithReason,
    [ActionTypes.REJECT_CLAIM_REVIEW_WITH_REASON_SUCCESS]:
        rejectClaimReviewWithReasonSuccess,
    [ActionTypes.REJECT_CLAIM_REVIEW_WITH_REASON_FAILED]:
        rejectClaimReviewWithReasonFailed,
    [ActionTypes.CHANGE_CLAIM_REVIEW_ANSWERS_STATE]:
        changeClaimReviewAnswerStateAction,
    [ActionTypes.CHANGE_CLAIM_REVIEW_STATE]: changeClaimReviewStateAction,
});

export default claimReview;
